/**
 * @file 解説のグラフの指定 (宣言型、API の StudyGuidePlot) を FunctionPlot が描ける形 (関数) にする。
 *
 * @what: 多項式 (coeffs) と名前つき関数 (a·f(b·x + c) + d) から x → y の関数を組み立て、塗りの
 *   指定 (曲線の下、弦と曲線の間) を FunctionPlot の PlotSpec に写す。
 * @why: 共通データは Firestore に置くので関数を持てない。関数の組み立てをここに閉じ、描画の本体
 *   (FunctionPlot) は試作のまま使う。標本の刻みはこの層が決める (サーバは範囲だけを検査する)。
 * @failure: 未知の関数名は恒等的に NaN を返す関数にする (FunctionPlot が値域外として線を切る)。
 */

import type { PlotSpec } from "../aids/lib/FunctionPlot";
import type { StudyGuideCurve, StudyGuidePlot } from "../api/studyWorks";

const NAMED: Record<string, (x: number) => number> = {
  sin: Math.sin,
  cos: Math.cos,
  exp: Math.exp,
  log: Math.log,
  sqrt: Math.sqrt,
  inv: (x) => 1 / x,
  tanh: Math.tanh,
};

/** 曲線の指定から x → y の関数を作る。 */
export function curveFunction(c: StudyGuideCurve): (x: number) => number {
  if (c.kind === "poly") {
    const coeffs = c.coeffs ?? [];
    return (x) => coeffs.reduce((acc, coef, i) => acc + coef * x ** i, 0);
  }
  const f = NAMED[c.name ?? ""];
  if (!f) return () => Number.NaN;
  const { a, b, c: cc, d } = c;
  return (x) => a * f(b * x + cc) + d;
}

/** 弦と曲線の間を塗るときの、曲線側の標本点の数である。 */
const POLYGON_SAMPLES = 40;

export function toPlotSpec(p: StudyGuidePlot): PlotSpec {
  const fns = p.curves.map(curveFunction);
  const spec: PlotSpec = {
    xRange: p.x_range,
    yRange: p.y_range,
    curves: p.curves.map((c, i) => ({
      f: fns[i]!,
      ...(c.label ? { label: c.label } : {}),
      ...(c.dashed ? { dashed: true } : {}),
    })),
    caption: p.caption ?? "",
  };
  if (p.area_under) {
    const f = fns[p.area_under.curve];
    if (f) {
      spec.areaUnder = {
        f,
        from: p.area_under.from,
        to: p.area_under.to,
        ...(p.area_under.label ? { label: p.area_under.label } : {}),
      };
    }
  }
  if (p.polygon) {
    const points: [number, number][] = [...p.polygon.points];
    const f = p.polygon.curve >= 0 ? fns[p.polygon.curve] : undefined;
    if (f) {
      const { curve_from: from, curve_to: to } = p.polygon;
      for (let i = 0; i <= POLYGON_SAMPLES; i++) {
        const x = from + ((to - from) * i) / POLYGON_SAMPLES;
        points.push([x, f(x)]);
      }
    }
    spec.polygon = { points, ...(p.polygon.label ? { label: p.polygon.label } : {}) };
  }
  if (p.circles && p.circles.length > 0) {
    spec.circles = p.circles.map((c) => ({
      cx: c.cx,
      cy: c.cy,
      r: c.r,
      ...(c.label ? { label: c.label } : {}),
      ...(c.dashed ? { dashed: true } : {}),
    }));
  }
  if (p.segments && p.segments.length > 0) {
    spec.segments = p.segments.map((s) => ({
      from: s.from,
      to: s.to,
      ...(s.label ? { label: s.label } : {}),
      ...(s.dashed ? { dashed: true } : {}),
    }));
  }
  if (p.points && p.points.length > 0) {
    spec.points = p.points.map((pt) => ({
      x: pt.x,
      y: pt.y,
      ...(pt.label ? { label: pt.label } : {}),
    }));
  }
  if (p.x_ticks) spec.xTicks = p.x_ticks.map((t) => ({ x: t.value, label: t.label }));
  if (p.y_ticks) spec.yTicks = p.y_ticks.map((t) => ({ y: t.value, label: t.label }));
  return spec;
}
